import type { FC, ReactNode } from 'react';
import { memo, useMemo } from 'react';

import type { ProviderWithValue } from './createGenericContext';

interface Props {
    providers: MultiContextProviderType[];
    children: ReactNode;
}

type FCWithChildren = FC<{ children: ReactNode }>;

export type MultiContextProviderType = ProviderWithValue | FCWithChildren;

function isContextWithValue(context: MultiContextProviderType): context is ProviderWithValue {
    return 'defaultValue' in context;
}

const ContextWrapper: FCWithChildren = memo(({ children }) => <>{children}</>);

const composeProviders = (wrappers: MultiContextProviderType[]): FCWithChildren => {
    return wrappers.reduce<FCWithChildren>((Acc, Context): FCWithChildren => {
        if (isContextWithValue(Context)) {
            return (props) => (
                <Context.Provider value={Context.defaultValue}>
                    <Acc {...props} />
                </Context.Provider>
            );
        }

        return (props) => (
            <Context>
                <Acc {...props} />
            </Context>
        );
    }, ContextWrapper);
};

/**
 * Provides wrapper for providers, so they can be configured in 'flat' manner, and we
 * can avoid unnecessary nesting.
 */
export const MultiContextProvider: FC<Props> = memo(({ providers, children }) => {
    const ComposedProviders = useMemo(() => composeProviders(providers), [providers]);

    return <ComposedProviders>{children}</ComposedProviders>;
});
